import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI, subscriptionAPI } from "../lib/api";

const AuthContext = createContext(null);

// Admin email constant (should match backend)
const ADMIN_EMAIL = "management@rmbcservices.com";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from cookie via /auth/me
  useEffect(() => {
    authAPI
      .getMe()
      .then((res) => {
        setUser(res.data.user);
        setSubscription(res.data.subscription);
      })
      .catch(() => {
        setUser(null);
        setSubscription(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    setUser(res.data.user);
    setSubscription(res.data.subscription);
    return res.data;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    setUser(res.data.user);
    setSubscription(res.data.subscription);
    return res.data;
  };

  const agreeToTerms = async () => {
    try {
      const res = await authAPI.consent();
      if (res.data?.success) {
        setUser(res.data.user);
        return true;
      }
    } catch (err) {
      console.error("Failed to record consent:", err);
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    setSubscription(null);
  };

  const updateProfile = async (data) => {
    const res = await authAPI.updateProfile(data);
    setUser(res.data);
    return res.data;
  };

  const refreshSubscription = async () => {
    try {
      const res = await subscriptionAPI.getStatus();
      setSubscription(res.data);
      return res.data;
    } catch (err) {
      console.error("Failed to refresh subscription:", err);
      return null;
    }
  };

  const isSubscriptionActive = subscription?.active === true;
  const isAdmin = user?.email === ADMIN_EMAIL;
  const trialExpired = subscription?.trial_expired === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        subscription,
        loading,
        isAuthenticated: !!user,
        isSubscriptionActive,
        isAdmin,
        trialExpired,
        login,
        register,
        logout,
        updateProfile,
        refreshSubscription,
        agreeToTerms,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};  